import React, { useEffect, useState } from 'react';
import { MdCheckCircle, MdArrowBack, MdArrowForward, MdInfoOutline, MdClose } from 'react-icons/md';
import { useFormContext } from '../../context/FormContext';
import { useReadOnly } from '../../hooks/useReadOnly';
import { SignupLayout } from '../ui/SignupLayout';
import { OptionCard } from '../ui/OptionCard';
import { createQuote, saveQuote, updateQuote } from '../services/QuoteService';
import { Spinner } from 'reactstrap';
import { generateNmDocument } from '../services/DocumentService';
import { formatCurrency } from '../util/formatters';
import Session from '../auth/Session';
import { SessionExpiredError } from '../services/apiClient';


// Maps a NormanMax trigger_type string to the display labels.
const TRIGGER_META = {
    single: {
        badge: 'SINGLE DATA POINT',
        name: 'Proxy wind',
        description: 'Wind speed data from weather monitoring sites near your property.',
    },
    dual: {
        badge: 'DOUBLE DATA POINT',
        name: 'Proxy wind + anemometer',
        description: 'Wind speed data from both inland weather sites and coastal anemometer stations.',
    },
    triple: {
        badge: 'TRIPLE DATA POINT',
        name: 'Proxy wind + anemometer + storm track',
        description:
            'Wind speed data, coastal anemometer readings, and real-time storm tracking based on hurricane category near your property.',
    },
};

// Loading skeleton
function LoadingSkeleton() {
    return (
        <div className="dp-loading" aria-label="Loading rate options…">
            <Spinner />
            <p className="dp-loading__text">Checking eligibility and fetching your rate options…</p>
        </div>
    );
}

// Simple info modal
function InfoModal({ title, children, onClose }) {
    return (
        <div className="info-modal__overlay" onClick={onClose}>
            <div
                className="info-modal"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="info-modal__header">
                    <p className="info-modal__title">{title}</p>
                    <button
                        className="info-modal__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <MdClose />
                    </button>
                </div>
                <div className="info-modal__body">{children}</div>
            </div>
        </div>
    );
}

// Trigger option card
function TriggerCard({ option, selected, onSelect, onInfoClick  }) {
    const meta = TRIGGER_META[option.trigger_type] || {
        badge: option.trigger_type.toUpperCase(),
        name: option.trigger_name,
        description: '',
    };

    const showInfo = option.trigger_type === 'dual';

    return (
        <OptionCard selected={selected} onChange={() => onSelect(option.trigger_type)}>
            <div>
                <span className="dp-card__badge">{meta.badge}</span>
                <p className="dp-card__name" style={{display: 'flex'}}>
                    {meta.name}
                    {showInfo && (
                        <button
                            type="button"
                            className="dp-card__info-btn"
                            aria-label="What is an anemometer?"
                            onClick={(e) => {
                                e.stopPropagation(); // don't select the card
                                onInfoClick();
                            }}
                        >
                            <MdInfoOutline size={16} />
                        </button>
                    )}
                </p>
                <p className="dp-card__desc">{meta.description}</p>
                <span className="dp-card__premium">
                    Total annual premium: {formatCurrency(option.total_amount_due)}
                </span>
            </div>
        </OptionCard>
    );
}

// Main component
export function DataPointSelection({ history }) {
    const { formData, setField, setFields, setStep } = useFormContext();
    const { isReadOnly } = useReadOnly();

    const [status, setStatus] = useState('loading'); // loading | success | referred | error
    const [errorMessage, setErrorMessage] = useState('');
    const [payoutLoading, setPayoutLoading] = useState(false);

    const [showAnemometerModal, setShowAnemometerModal] = useState(false);

    // Selected option premium
    const selectedOption = formData.RateOptions?.find(
        (o) => o.trigger_type === formData.SelectedTriggerType
    );

    // Fetch quote on mount
    useEffect(() => {
        // If read-only - reutrn
        if (isReadOnly) {
            setStatus('success');
            return; // skip validation, API calls, everything
        }
        // If we already have rate options in context (e.g. user navigated back),
        // skip the API call and show results immediately.
        const previousStep = formData.currentStep;
        setStep('dataPoints');

        if (previousStep !== 'eligibility') {
            // Coming from a later step — use cached data
            if (formData.RateOptions?.length > 0 && formData.QuoteNumber) {
                setStatus('success');
            } else if (formData.QuoteNumber && formData.SelectedTriggerType) {
                setStatus('success');
            } else {
                history.push('/signup/eligibility');
            }
            return;
        }

        let cancelled = false;

        async function fetchQuote() {
            try {
                const data = await createQuote(formData);

                if (cancelled) return;

                if (!data.success) {
                    setErrorMessage(data.error_message || 'An unexpected error occurred.');
                    setStatus('error');
                    return;
                }

                if (data.status === 'Referred') {
                    setStatus('referred');
                    return;
                }

                const updatedFields = {
                    QuoteNumber: data.quote_number,
                    RateOptions: data.rate_options || [],
                    SelectedTriggerType: data.rate_options?.[0]?.trigger_type || '',
                    SelectedTotalPremium: data.rate_options?.[0]?.total_amount_due || 0,
                    EffectiveDate: data.effective_date,
                }

                setFields(updatedFields);

                // Persist to DB
                // saveQuote is called here - this is the first
                // time we have a QuoteNumber and enough meaningful data to persist.
                try {
                    const saveResult = await saveQuote({
                        ...formData,
                        ...updatedFields,
                    });
 
                    if (saveResult?.quoteId) {
                        setField('QuoteId', saveResult.quoteId);
                    }
                } catch (saveErr) {
                    console.error('Failed to persist quote to DB:', saveErr);
                }

                setStatus('success');
            } catch (err) {
                if (cancelled) return;

                if (err.code === 'OUTSIDE_COVERAGE_AREA') {
                    history.push('/signup/not-eligible'); 
                    return;
                }

                history.push('/signup/not-eligible');

                setErrorMessage('Something went wrong. Please try again or contact support.');
                setStatus('error');
            }
        }

        fetchQuote();

        return () => {
            cancelled = true;
        };
    }, []);

    // Navigation
    const handleBack = () => {
        history.push('/signup/eligibility');
    };

    const handleContinue = async () => {
        if (isReadOnly) {
            history.push('/signup/confirm-details');
            return;
        }
        if (!formData.SelectedTriggerType) return;

        // Update DB with selected trigger type before navigating
        if (formData.QuoteId) {
            try {
                await updateQuote(formData.QuoteId, formData);
            } catch (err) {
                if (err instanceof SessionExpiredError) return;
                console.error('Failed to update quote on continue:', err);
            }
        }

        if (formData.QuoteId) {
            // generate quote document
            generateNmDocument(formData.QuoteId, 'QUOTE').catch(err =>
                console.error('Failed to generate NormanMax QUOTE document:', err)
            );
        }
        history.push('/signup/confirm-details');
    };

    const handleViewPayoutTable = async () => {
        setPayoutLoading(true);
        try {
            const token = Session.getToken();
            const response = await fetch('api/SamplePolicy/GetPayoutTable', {
                method: 'GET',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
                console.error('Could not load payout table.');
                return;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            console.error('Could not load payout table.', err);
        } finally {
            setPayoutLoading(false);
        }
    };

    // return 
    return (
        <SignupLayout currentStep={2} totalSteps={3} stepLabel="Confirm details">

            {/* Loading */}
            {status === 'loading' && (
                <>
                    <LoadingSkeleton />
                    {/* Info banner */}
                    <div className="security-banner" style={{ borderColor: 'var(--brand-primary)' }}>
                        <MdInfoOutline size={20} className="security-banner__icon" />
                        <div className="security-banner__text">
                            <strong>Please keep this window open</strong>
                            <p>
                                Confirming eligibility and generating rate options may take up to{' '}
                                <strong>45 seconds.</strong>
                            </p>
                        </div>
                    </div>
                </>
            )}

            {/* Referred */}
            {status === 'referred' && (
                <div className="dp-referred">
                    <p className="dp-referred__message">
                        Your quote has been referred for additional review. Our team will be in touch
                        shortly. In the meantime, please call us at{' '}
                        <a href="tel:8772695422">877-269-5422</a> if you need immediate assistance.
                    </p>
                </div>
            )}

            {/* General error */}
            {status === 'error' && (
                <div className="dp-error">
                    <p className="dp-error__message">{errorMessage}</p>
                    <p className="dp-error__help">
                        Need help? Call us at <a href="tel:8772695422">877-269-5422</a>.
                    </p>
                    <div className="signup-nav" style={{ marginTop: 'var(--space-lg)' }}>
                        <button className="btn btn--inverse-cta" onClick={handleBack}>
                            <MdArrowBack /> Back
                        </button>
                    </div>
                </div>
            )}

            {/* Success */}
            {status === 'success' && (
                <>
                    {/* Eligibility success banner */}
                    <div className="eligibility-success">
                        <MdCheckCircle size={20} className="eligibility-success__icon" />
                        <div className="eligibility-success__text">
                            <strong>Your property is eligible for coverage!</strong>
                            <p>Keep going to see your options and get a policy in minutes.</p>
                        </div>
                    </div>

                    {/* Heading */}
                    {!isReadOnly && formData.RateOptions?.length > 0 && (
                        <>
                            <h1 className="signup-heading" style={{ marginTop: 0 }}>
                                Select your data point options.
                            </h1>
                            <p className="signup-section__desc">
                                Your data point determines what conditions must be met for a payout. The more
                                data points included in your coverage, the more precisely your payout is triggered.
                            </p>
                        </>
                    )}

                    {!isReadOnly && formData.RateOptions?.length > 0 && (
                        <p className="small" style={{ marginBottom: 'var(--space-lg)' }}>
                            View{' '}
                            <button
                                className="finalize__link-btn"
                                onClick={handleViewPayoutTable}
                                disabled={payoutLoading}
                            >
                                {payoutLoading ? 'Loading…' : 'sample payout tables'}
                            </button>{' '}
                            to see how each data point affects your payout.
                        </p>
                    )}

                    {/* Trigger option cards */}
                    {isReadOnly || !formData.RateOptions?.length ? (
                        <div className="summary-card" style={{ marginBottom: 'var(--space-xl)' }}>
                            <p className="summary-card__title">Selected coverage option</p>
                            <div className="summary-row">
                                <span className="summary-row__label">Data point option</span>
                                <span className="summary-row__value">
                                    {TRIGGER_META[formData.SelectedTriggerType]?.name || formData.SelectedTriggerType || '—'}
                                </span>
                            </div>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="summary-row__label">Total annual premium</span>
                                <span className="summary-row__value">
                                    {formData.PolicyTotalPremium
                                        ? formatCurrency(formData.PolicyTotalPremium)
                                        : formData.SelectedTotalPremium
                                            ? formatCurrency(formData.SelectedTotalPremium)
                                            : '-'}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="dp-options">
                            {formData.RateOptions.map((option) => (
                                <TriggerCard
                                    key={option.trigger_type}
                                    option={option}
                                    selected={formData.SelectedTriggerType === option.trigger_type}
                                    onInfoClick={() => setShowAnemometerModal(true)}
                                    onSelect={(type) => {
                                        const option = formData.RateOptions.find(o => o.trigger_type === type);
                                        setFields({
                                            SelectedTriggerType: type,
                                            SelectedTotalPremium: option?.total_amount_due || 0,
                                        });
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* Total annual premium */}
                    {!isReadOnly && formData.RateOptions?.length > 0 && (
                        <div className="dp-total">
                            <h2 className="dp-total__heading">Your total annual premium</h2>
                            <p className="dp-total__desc">
                                Your total annual premium based on the coverage and data points you selected is:
                            </p>
                            <p className="dp-total__label">TOTAL ANNUAL PREMIUM</p>
                            <p className="dp-total__amount">
                                {selectedOption ? 
                                formatCurrency(selectedOption.total_amount_due) 
                                : formData.SelectedTotalPremium
                                    ? formatCurrency(formData.SelectedTotalPremium) 
                                    : '-'}
                            </p>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="signup-nav">
                        <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                            <MdArrowBack /> Back
                        </button>
                        <button
                            className="btn signup-cta"
                            onClick={handleContinue}
                            disabled={!formData.SelectedTriggerType}
                        >
                            Continue <MdArrowForward />
                        </button>
                    </div>
                </>
            )}

            {showAnemometerModal && (
                <InfoModal
                    title="Anemometer"
                    onClose={() => setShowAnemometerModal(false)}
                >
                    An anemometer is an instrument that measures wind speed and wind pressure.
                </InfoModal>
            )}
        </SignupLayout>
    );
}